import React, {useState, useEffect} from 'react';
import {Switch, Route} from 'react-router-dom';

import PropTypes from 'prop-types';

//MUI Components
import {ThemeProvider} from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import {Typography, useMediaQuery, useTheme} from '@mui/material';

import Theme from './Theme';

// Custom Components
import AuthError from './components/auth-error';
import AppHeader from './components/app-header';
import {StyledPageContainer} from 'components/styled';
import SideBar from 'components/side-bar';
import PageNotFound from 'pages/PageNotFound';

import {useGetConfigQuery, useLazyGetJwsKeysQuery, useLazyGetUserInfoQuery} from 'api';
import {
  getJwtReponsePromise,
  requestAccessToken,
  getUrlParameterByName,
  validateAuthorizationState,
  getRightsFromScopeUrlParameter
} from './contexts/openIdConnect';

// Utils
import {areDefined, areScopesAvailable} from 'utils';

// REDUX
import {connect} from 'react-redux';
import {
  setConfig,
  setUserInfo,
  setAccessTokenIsAvailable,
  setAuthError,
  setRights,
  setLoginDateTime,
  accessTokenSelector,
  accessTokenIsAvailableSelector,
  accessTokenExpirationSelector,
  accessTokenTypeSelector,
  loginDateTimeSelector,
  authErrorSelector,
  userInfoSelector,
  rightsSelector
} from 'redux/slices/appSlice';

import {ROUTES} from 'routes.js';

const App = ({
  accessToken,
  accessTokenIsAvailable,
  accessTokenExpiration,
  accessTokenType,
  loginDateTime,
  authError,
  userInfo,
  rights,
  setAppAccessTokenIsAvailable,
  setAppLoginDateTime,
  setAppAuthError,
  setAppConfig,
  setAppUserInfo,
  setAppRights
}) => {
  const [alertIsShown, setAlertIsShown] = useState(true);
  const [drawerIsShown, setDrawerIsShown] = useState(false);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const {data: config, isError} = useGetConfigQuery();
  const [errorParameter] = useState(getUrlParameterByName('error'));
  const [getJwsKey, {data: jwsKey}] = useLazyGetJwsKeysQuery();
  const [getUserInfo, {data: encodedUserInfo}] = useLazyGetUserInfoQuery();
  const mobile = useMediaQuery(useTheme().breakpoints.down('md'));

  useEffect(() => {
    if (config) {
      if (encodedUserInfo && jwsKey) {
        getJwtReponsePromise(encodedUserInfo, jwsKey).then((decodedUserInfo) => {
          if (decodedUserInfo) {
            setAppUserInfo(decodedUserInfo);
          }
        });
      }
    }
  }, [jwsKey, encodedUserInfo]);

  useEffect(() => {
    if (config) {
      setAppConfig(config);

      if (accessTokenIsAvailable) return;
      if (config.openId && !rights) setAppRights(getRightsFromScopeUrlParameter(config.openId.scope));
      if (errorParameter) {
        setAppAuthError({is: true, description: errorParameter});
        return;
      }

      if (areDefined(accessToken, accessTokenExpiration, accessTokenType)) {
        setAppLoginDateTime(Math.round(Date.now() / 1000));
        if (rights) {
          if (validateAuthorizationState(accessTokenType, loginDateTime, accessTokenExpiration, accessToken, rights)) {
            setAppAccessTokenIsAvailable(true);
          } else {
            setAppAuthError({is: true, errorScope: !areScopesAvailable(rights), description: errorParameter});
          }
        }
      } else {
        if (config.openId) {
          requestAccessToken(config.openId);
        }
      }
    }
    if (isError) {
      setAppAuthError({is: true, description: 'Failed load application config.'});
    }
  }, [config, rights, isError]);

  useEffect(() => {
    if (accessTokenIsAvailable) {
      getJwsKey();
      getUserInfo();
    }
  }, [accessTokenIsAvailable]);

  useEffect(() => {
    if (rights && accessTokenExpiration) {
      setTimeout(() => {
        if (!validateAuthorizationState(accessTokenType, loginDateTime, accessTokenExpiration, accessToken, rights)) {
          setAppAuthError({
            is: true,
            invalidTokenError: true,
            errorScope: !areScopesAvailable(rights),
            description: errorParameter
          });
        }
      }, parseInt(accessTokenExpiration) * 1000);
    }
  }, [accessTokenExpiration, rights]);

  useEffect(() => {
    if (rights) {
      setFilteredRoutes(ROUTES.filter((route) => rights[route.rightsTitle]));
      if (Object.values(rights).every((value) => value === true)) {
        setDrawerIsShown(true);
      }
    }
  }, [rights]);

  return (
    <ThemeProvider theme={Theme}>
      {authError.is ? (
        <AuthError />
      ) : (
        accessTokenIsAvailable && (
          <>
            <AppHeader userInfo={userInfo} isBurgerButtonShown={mobile && drawerIsShown} />
            {drawerIsShown && <SideBar />}
            <StyledPageContainer container style={{paddingLeft: (mobile || !drawerIsShown) && 0}}>
              {alertIsShown && (
                <Alert
                  sx={{width: '100%'}}
                  severity="info"
                  onClose={() => {
                    setAlertIsShown(false);
                  }}
                >
                  <Typography>This application contains highly confidential information.</Typography>
                </Alert>
              )}
              <Switch>
                {filteredRoutes.map(({path, page}) => (
                  <Route key={path} exact path={path} component={page} />
                ))}
                <Route path="*">
                  <PageNotFound />
                </Route>
              </Switch>
            </StyledPageContainer>
          </>
        )
      )}
    </ThemeProvider>
  );
};

App.propTypes = {
  accessTokenIsAvailable: PropTypes.bool,
  accessToken: PropTypes.string,
  accessTokenExpiration: PropTypes.string,
  accessTokenType: PropTypes.string,
  loginDateTime: PropTypes.number,
  authError: PropTypes.object,
  userInfo: PropTypes.object,
  rights: PropTypes.object,
  setAppAccessTokenIsAvailable: PropTypes.func,
  setAppLoginDateTime: PropTypes.func,
  setAppAuthError: PropTypes.func,
  setAppConfig: PropTypes.func,
  setAppUserInfo: PropTypes.func,
  setAppRights: PropTypes.func
};

const mapStateToProps = (state) => ({
  accessToken: accessTokenSelector(state),
  accessTokenIsAvailable: accessTokenIsAvailableSelector(state),
  accessTokenExpiration: accessTokenExpirationSelector(state),
  accessTokenType: accessTokenTypeSelector(state),
  loginDateTime: loginDateTimeSelector(state),
  authError: authErrorSelector(state),
  userInfo: userInfoSelector(state),
  rights: rightsSelector(state)
});

const mapDispatchToProps = (dispatch) => {
  return {
    setAppAccessTokenIsAvailable: (isAvailable) => dispatch(setAccessTokenIsAvailable(isAvailable)),
    setAppLoginDateTime: (dateTime) => dispatch(setLoginDateTime(dateTime)),
    setAppAuthError: (error) => dispatch(setAuthError(error)),
    setAppConfig: (config) => dispatch(setConfig(config)),
    setAppUserInfo: (userInfo) => dispatch(setUserInfo(userInfo)),
    setAppRights: (rights) => dispatch(setRights(rights))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
